import { TATTOO_CONSENT_FORM_API_URL, UPLOAD_IMAGE_API_URL, CUSTOMER_API_URL, PIERCING_CONSENT_FORM_API_URL, GENERATE_CONSENT_PDF_URL } from "../api/endpoints";
import { fetchApi } from "./fetch";
import { prepareImageForUpload } from "./imageUpload";
import { normalizePhoneNumber } from "./phone";

/**
 * Helper function to handle customer lookup and creation/update
 * @param {object} customerData - Customer data from form
 * @param {function} setError - Error callback function
 * @returns {Promise<string>} - Customer ID
 */
export const handleCustomerLookupForForm = async (customerData, setError) => {
  try {
    const formattedPhone = normalizePhoneNumber(customerData.phone);
    if (!formattedPhone) {
      throw new Error('Invalid phone number format. Please enter with country code (e.g., +91xxxxxxxxxx)');
    }
    
    // Query all customers
    const customers = await fetchApi(CUSTOMER_API_URL, { method: "GET" });
    const existingCustomer = Array.isArray(customers) 
      ? customers.find(c => c.phone === formattedPhone)
      : null;

    if (existingCustomer) {
      // Update existing customer with address and heard_about_us if provided
      const updateData = {};
      if (customerData.address) {
        updateData.address = customerData.address;
      }
      if (customerData.heardAboutUs && !existingCustomer.heardAboutUs) {
        updateData.heard_about_us = customerData.heardAboutUs;
      }
      
      if (Object.keys(updateData).length > 0) {
        await fetchApi(`${CUSTOMER_API_URL}/${existingCustomer.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        });
      }
      return existingCustomer.id;
    } else {
      // Create new customer
      const newCustomer = await fetchApi(CUSTOMER_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: customerData.name,
          date_of_birth: customerData.dob,
          phone: formattedPhone,
          email: customerData.email && customerData.email.trim() ? customerData.email.trim() : null,
          address: customerData.address,
          heard_about_us: customerData.heardAboutUs,
        }),
      });
      return newCustomer.id;
    }
  } catch (error) {
    // Check for response message in various possible locations
    const apiMessage = error.response?.message || error.data?.message || error.data?.error || error.message;
    const errorMessage = "Customer lookup/creation failed: " + apiMessage;
    setError(errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Helper function to upload image to Xano
 * @param {File} imageFile - Image file to upload
 * @param {function} setError - Error callback function
 * @returns {Promise<object>} - Upload response metadata
 */
const uploadImage = async (imageFile, setError) => {
  if (!imageFile) return null;
  
  try {
    const preparedFile = await prepareImageForUpload(imageFile);
    if (!preparedFile?.size) {
      throw new Error("No file uploaded");
    }

    const imageForm = new FormData();
    imageForm.append("content", preparedFile, preparedFile.name);
    
    return await fetchApi(UPLOAD_IMAGE_API_URL, {
      method: "POST",
      body: imageForm,
    });
  } catch (error) {
    // Check for response.message first, then fall back to error.message
    const apiMessage = error.response?.message || error.data?.message || error.data?.error || error.message;
    const errorMessage = "Image upload failed: " + apiMessage;
    setError(errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Helper function to convert form boolean values to database format
 * @param {string} value - Form value ("yes"/"no")
 * @returns {boolean} - Boolean value
 */
const convertToBoolean = (value) => value === "yes";

/**
 * Helper function to create health information payload
 * @param {object} form - Form data
 * @returns {object} - Health information object
 */
const createHealthInfo = (form) => ({
  hasMedications: convertToBoolean(form.medications),
  medicationsList: form.medicationsList,
  hasAllergies: convertToBoolean(form.allergies),
  allergiesList: form.allergiesList,
  hasMedicalConditions: convertToBoolean(form.medicalConditions),
  medicalConditionsList: form.medicalConditionsList,
  alcoholDrugs: convertToBoolean(form.alcoholDrugs),
  pregnantNursing: convertToBoolean(form.pregnantNursing),
});

/**
 * Helper function to handle referral tracking
 * @param {string} currentCustomerId - ID of the current customer (who was referred)
 * @param {object} form - Form data
 * @param {function} setError - Error callback function
 * @returns {Promise<void>}
 */
const handleReferralTracking = async (currentCustomerId, form, setError) => {
  if (form.heardAboutUs === "friend-recommendation" && form.referralPhone) {
    try {
      const formattedReferralPhone = normalizePhoneNumber(form.referralPhone);
      if (!formattedReferralPhone) {
        return; // Skip referral tracking if phone is invalid
      }
      
      // Find the referring customer
      const customers = await fetchApi(CUSTOMER_API_URL, { method: "GET" });
      const referringCustomer = Array.isArray(customers) 
        ? customers.find(c => c.phone === formattedReferralPhone)
        : null;

      if (referringCustomer) {
        // Update the current customer's record to include referred_by_customer_id
        await fetchApi(`${CUSTOMER_API_URL}/${currentCustomerId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            referred_by_customer_id: referringCustomer.id
          }),
        });
      }
    } catch (error) {
      // console.error("Referral tracking failed:", error);
    }
  }
};

/**
 * Submits the tattoo consent form, handling customer lookup/creation and image upload.
 * @param {object} form - The form data from the component state
 * @param {function} setLoading - Callback to set loading state
 * @param {function} setError - Callback to set error state
 * @returns {Promise<object>} - The API response from consent form submission
 */
export async function submitTattooConsentForm(form, setLoading, setError) {
  setLoading(true);
  setError(null);

  try {
    // Step 1: Handle customer lookup/creation
    const customerId = await handleCustomerLookupForForm(form, setError);

    // Step 2: Upload image if provided
    const tattooDesignMeta = await uploadImage(form.tattooDesign, setError);

    // Step 3: Create consent form payload
    const healthInfo = createHealthInfo(form);
    const payload = {
      customerId: Number(customerId) || undefined,
      tattooLocation: form.tattooLocation,
      tattooDesign: tattooDesignMeta?.path || tattooDesignMeta?.url || undefined,
      tattooArtist: form.tattooArtist,
      tattooDate: form.tattooDate,
      // Backend expects booleans and optional lists in camelCase
      signed: Boolean(form.agree),
      dateSigned: new Date().toISOString(),
      agree: Boolean(form.agree),
      ...healthInfo,
    };

    // Step 4: Submit consent form
    const response = await fetchApi(TATTOO_CONSENT_FORM_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    // Step 5: Handle referral tracking
    await handleReferralTracking(customerId, { ...form, service_type: "tattoo" }, setError);

    // Step 6: Generate PDF and send via WhatsApp
    /*
    try {
      await fetchApi(GENERATE_CONSENT_PDF_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formData: form,
          formType: "tattoo"
        }),
      });
    } catch (pdfError) {
      console.error("PDF generation failed:", pdfError);
      // Don't fail the entire submission if PDF generation fails
    }
    */ // PDF generation and WhatsApp sending is disabled for now

    setLoading(false);
    return response;
  } catch (error) {
    setLoading(false);
    // Error is already set by helper functions, just re-throw
    throw error;
  }
}

/**
 * Submits the piercing consent form, handling customer lookup/creation.
 * @param {object} form - The form data from the component state
 * @param {function} setLoading - Callback to set loading state
 * @param {function} setError - Callback to set error state
 * @returns {Promise<object>} - The API response from consent form submission
 */
export async function submitPiercingConsentForm(form, setLoading, setError) {
  setLoading(true);
  setError(null);

  try {
    // Step 1: Handle customer lookup/creation
    const customerId = await handleCustomerLookupForForm(form, setError);

    // Step 2: Create consent form payload
    const healthInfo = createHealthInfo(form);
    const payload = {
      customerId: Number(customerId) || undefined,
      piercingType: form.piercingType,
      piercingSubtype: form.piercingSubtype,
      piercingArtist: form.piercingArtist,
      piercingDate: form.piercingDate,
      agree: Boolean(form.agree),
      ...healthInfo,
    };

    // Step 3: Submit consent form
    const response = await fetchApi(PIERCING_CONSENT_FORM_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    // Step 4: Handle referral tracking
    await handleReferralTracking(customerId, { ...form, service_type: "piercing" }, setError);

    // Step 5: Generate PDF and send via WhatsApp
    try {
      await fetchApi(GENERATE_CONSENT_PDF_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formData: form,
          formType: "piercing"
        }),
      });
    } catch (pdfError) {
      // console.error("PDF generation failed:", pdfError);
    }

    setLoading(false);
    return response;
  } catch (error) {
    setLoading(false);
    // Error is already set by helper functions, just re-throw
    throw error;
  }
} 